import { useState } from 'preact/hooks';
import { HardDrives, Cpu, Pulse, Lightning, Coins, CurrencyDollar, Cube, Warning } from '@phosphor-icons/react';
import { StatCard } from './StatCard.js';
import { FarmTable } from './FarmTable.js';
import { FarmDetails } from './FarmDetails.js';
import { ProfitCalculator } from './ProfitCalculator.js';
import { StatCardSkeleton } from '../shared/Skeleton.js';
import type { ProcessedFarm } from '../../hooks/useFarms.js';
import type { PoolResponse } from '../../services/api.js';
import { formatHashrate, formatPower } from '../../utils/format.js';
import { useDetox } from '../../utils/detox.js';

interface OverviewPageProps {
  farms: ProcessedFarm[];
  pool: PoolResponse | null;
  loading: boolean;
  coreTempThreshold: number;
  memoryTempThreshold: number;
  totalPower: number;
  electricityRate: number;
  usdRub: number;
}

export function OverviewPage({ farms, pool, loading, coreTempThreshold, memoryTempThreshold, totalPower, electricityRate, usdRub }: OverviewPageProps) {
  const [selectedFarm, setSelectedFarm] = useState<ProcessedFarm | null>(null);

  const activeFarms = farms.filter((f) => f.isOnline);
  const offlineFarms = farms.filter((f) => !f.isOnline);
  const totalGPUs = activeFarms.reduce((s, f) => s + f.gpuCount, 0);
  const totalHashrate = activeFarms.reduce((s, f) => s + f.hashrate, 0);
  const hr = formatHashrate(totalHashrate);
  const pw = formatPower(totalPower);

  // Total power from ALL farms (including offline) for profit calculator
  const allFarmsPower = farms.reduce((s, f) => {
    const power = (f.raw.stats?.params?.power ?? []).filter((v: number) => v !== 0).reduce((a: number, b: number) => a + b, 0);
    return s + power;
  }, 0) || totalPower;

  const coinSymbol = pool?.pool.coinSymbol ?? 'XEL';
  // Детокс: в закрытые дни цена монеты скрыта за звёздочками
  const detox = useDetox();
  const coinPrice = !detox.open
    ? '✱✱✱'
    : pool ? `$${parseFloat(pool.pool.coinPriceUsd).toFixed(2)}` : '$0.00';
  const blocksFound = pool?.miner.totalBlocksFound ?? 0;

  // Power color class
  const powerClass = totalPower >= 2000 ? 'temp-hot' : totalPower >= 1000 ? 'temp-warm' : '';

  const handleSelectFarm = (farm: ProcessedFarm) => {
    setSelectedFarm((prev) => (prev?.id === farm.id ? null : farm));
  };

  return (
    <>
      <div class="stats-grid">
        {loading && farms.length === 0 ? (
          Array.from({ length: 7 }, (_, i) => <StatCardSkeleton key={i} />)
        ) : (
          <>
            <StatCard
              icon={offlineFarms.length > 0 ? <Warning size={18} weight="duotone" /> : <HardDrives size={18} weight="duotone" />}
              accent={offlineFarms.length > 0 ? 'red' : 'lime'}
              value={`${activeFarms.length}/${farms.length}`}
              label="Фермы онлайн"
              sub={offlineFarms.length > 0 ? `${offlineFarms.length} offline` : undefined}
            />
            <StatCard icon={<Pulse size={18} weight="duotone" />} accent="purple" value={`${hr.value} ${hr.unit}`} label="Хэшрейт" />
            <StatCard icon={<Lightning size={18} weight="duotone" />} accent="amber" value={`${pw.value} ${pw.unit}`} label="Потребление" valueClass={powerClass} />
            <StatCard icon={<Cpu size={18} weight="duotone" />} accent="cyan" value={String(totalGPUs)} label="GPU устройств" />
            <StatCard icon={<Coins size={18} weight="duotone" />} accent="teal" value={coinSymbol} label="Монета" />
            <StatCard icon={<CurrencyDollar size={18} weight="duotone" />} accent="blue" value={coinPrice} label="Цена монеты" />
            <StatCard icon={<Cube size={18} weight="duotone" />} accent="pink" value={String(blocksFound)} label="Блоков найдено" />
          </>
        )}
      </div>

      {/* Offline Alert Banner */}
      {offlineFarms.length > 0 && (
        <div class="offline-banner">
          <Warning size={18} weight="duotone" />
          <div>
            <strong>{offlineFarms.length} {offlineFarms.length === 1 ? 'ферма' : offlineFarms.length < 5 ? 'фермы' : 'ферм'} offline</strong>
            <span class="offline-banner-names">{offlineFarms.map((f) => f.name).join(', ')}</span>
          </div>
        </div>
      )}

      {/* Mining Farms */}
      <div class="data-card">
        <div class="data-card-header">
          <HardDrives size={16} />
          <span>Майнинг фермы</span>
        </div>
        <div class="data-card-body" style={{ padding: 0 }}>
          <FarmTable
            farms={farms}
            loading={loading}
            selectedFarmId={selectedFarm?.id ?? null}
            onSelectFarm={handleSelectFarm}
          />
        </div>
      </div>

      {selectedFarm && (
        <FarmDetails
          farm={selectedFarm}
          coreTempThreshold={coreTempThreshold}
          memoryTempThreshold={memoryTempThreshold}
          onClose={() => setSelectedFarm(null)}
        />
      )}

      <div class="section-divider" />

      {/* Profit Calculator — use power from all farms (including offline, from raw stats) */}
      <ProfitCalculator pool={pool} totalPower={allFarmsPower} electricityRate={electricityRate} activeFarmsCount={activeFarms.length} usdRub={usdRub} />
    </>
  );
}
